import { Box } from '@material-ui/core'
import { CustomTooltip } from 'components/facets'
import { LanguageFlag } from 'components/facets/LanguageFlag'
import cardPreviewPlaceholder from 'img/cardPreviewPlaceholder.svg'
import { useStockStore } from 'lib/hooks/useStockStore'
import { currencySymbol } from 'lib/utils'
import * as React from 'react'
import {
  AppCurrency,
  CountryEdition,
  InventoryArticle,
  isSingle,
  PricedInventoryArticle,
  STOCK_TYPES,
} from 'shared'
import {
  BACKOFFICE_USER_STOCK,
  ESCAPE_STRING_FOR_EMPTY_COMMENT,
  LISTING_AND_APPRAISAL,
  LOCATIONS_PANEL,
  PRICE_SUGGEST_PRESENTER,
  PRICING_BOT,
} from 'utils/constants'
import { getProductImageUrl } from 'utils/imageUtils'
import { shallow } from 'zustand/shallow'
import './visual-article.css'
import { VisualArticleIconbar } from './VisualArticleIconbar'

export interface VisualArticleProps {
  openPriceSuggestDrawer?: (article: InventoryArticle, view?: 'buyPrice' | 'sellPrice') => void
  openReservedQuantityDrawer?: (article: InventoryArticle) => void
  openEditBuyPriceDrawer?: (article: InventoryArticle) => void
  selectArticle?: (article: InventoryArticle, shiftSelect?: boolean) => void
  rowHeight?: number
  revertArticleEdit?: (article: InventoryArticle) => void
  showLocations?: (article: InventoryArticle) => void
  article: InventoryArticle | PricedInventoryArticle
  displayOnly?: boolean
  onDeleteArticle?: (article: InventoryArticle) => void
  currency: AppCurrency
  parentEl?: string
  visualArticleSizeClass?: string
}

const showPrice = (
  price: InventoryArticle['price'] | undefined | null,
  currency: AppCurrency
): string => {
  if (price !== undefined && price !== null && price - Math.floor(price) !== 0) {
    return String((Math.round(price * 100) / 100).toFixed(2)) + currencySymbol(currency)
  }
  return price ? `${price}${currencySymbol(currency)}` : '???'
}

export const VisualArticle = React.memo(function VisualArticle({
  openPriceSuggestDrawer,
  openReservedQuantityDrawer,
  selectArticle,
  showLocations,
  article,
  displayOnly = false,
  onDeleteArticle,
  revertArticleEdit,
  visualArticleSizeClass,
  parentEl,
  currency,
}: VisualArticleProps): JSX.Element {
  const [activeStockType] = useStockStore((s) => [s.activeStockType], shallow)
  const { card, selected } = article
  const isEdited =
    parentEl !== LISTING_AND_APPRAISAL
      ? article.hasEditedAttribute() || article.hasEditedLocations()
      : article.isEdited()

  const articleComment = () => {
    if (
      article.newComment === ESCAPE_STRING_FOR_EMPTY_COMMENT ||
      article.comment === ESCAPE_STRING_FOR_EMPTY_COMMENT
    )
      return undefined
    return article.newComment ?? article.comment
  }

  const displayComment = () => {
    return (
      <div
        className="d-flex flex-row align-items-start comment-visual-article text-start"
        title={articleComment()}
      >
        <i
          className={`fa-light fa-message-dots comment-icon ${
            articleComment() === undefined || articleComment() === '' ? 'missing-comment' : ''
          }`}
        ></i>
        <span className="p-0 m-0" data-testid="article-comment">
          {articleComment()}
        </span>
      </div>
    )
  }

  const displayHeader = () => {
    if (article.countryEdition === CountryEdition.Regular) {
      return (
        <div
          className={`heading-container d-flex flex-column align-items-start pb-1 headingContainer`}
        >
          <div className="d-inline-block text-truncate w-100 fw-bold">
            <span>{card.name}</span>
          </div>
          <div className="d-inline-block text-truncate align-items-start w-100">
            <span> #{card.cn}</span>
            <span className="px-1"> - {card.set} -</span>
            <span className="text-truncate">{card.expansion}</span>
          </div>
        </div>
      )
    } else {
      return (
        <div
          className={`heading-container pb-1 headingContainer d-flex justify-content-between flex-row`}
        >
          <div className="d-flex flex-column align-items-start text-truncate">
            <div className="d-inline-block text-truncate w-100 fw-bold">
              <span>{card.name}</span>
            </div>
            <div className="d-inline-block text-truncate align-items-start w-100">
              <span> #{card.cn}</span>
              <span className="px-1"> - {card.set} -</span>
              <span className="text-truncate">{card.expansion}</span>
            </div>
          </div>
          <div style={{ padding: '4px' }} data-testid={article.countryEdition + '-edition'}>
            <CustomTooltip title={`${article.countryEdition} Edition`}>
              <LanguageFlag id={`${article.id}-flag`} code={article.countryEdition.toLowerCase()} />
            </CustomTooltip>
          </div>
        </div>
      )
    }
  }

  const visualAricleRootClassName = () => {
    let baseClass = `visual-article-container ${visualArticleSizeClass}`
    if (article.graded?.isGraded) baseClass = baseClass.concat(' graded')
    if (parentEl !== LOCATIONS_PANEL && parentEl !== PRICE_SUGGEST_PRESENTER) {
      if (selected) baseClass = baseClass.concat(' visual-article-selected')
    }
    if (isEdited && !displayOnly) baseClass = baseClass.concat(' visual-article-edited')
    if (parentEl === LOCATIONS_PANEL || parentEl === PRICE_SUGGEST_PRESENTER)
      baseClass = baseClass.concat(' force-auto-cursor')

    if (
      !displayOnly &&
      parentEl === LISTING_AND_APPRAISAL &&
      ((activeStockType === STOCK_TYPES.SINGLES && !isSingle(article.card)) ||
        (activeStockType === STOCK_TYPES.SEALED && isSingle(article.card)))
    ) {
      baseClass = baseClass.concat(' disable-visual-article')
    }

    return baseClass
  }

  const imgsrc = getProductImageUrl(card._id)
  return (
    <div
      data-testid={article.id}
      data-custom-id={article.ptProductId}
      className={visualAricleRootClassName()}
      onClick={(e) => {
        if (displayOnly) return
        if (e.shiftKey) document?.getSelection()?.removeAllRanges()
        // selecting articles leads nowhere in the pricing bot updates view, nor in the backoffice
        selectArticle &&
          parentEl !== PRICING_BOT &&
          parentEl !== BACKOFFICE_USER_STOCK &&
          selectArticle(article, e.shiftKey)
      }}
    >
      {displayHeader()}
      <Box
        className={`visual-article-root`}
        data-testid={`article-${article.idArticle}`}
        data-selected={selected ? true : undefined}
      >
        <div className="d-flex flex-column">
          <img
            src={imgsrc}
            className="article-image"
            onError={(e) => {
              e.currentTarget.src = cardPreviewPlaceholder
            }}
          />
          {!displayOnly && (
            <div className={parentEl === LOCATIONS_PANEL ? `d-none` : `mt-2`}>
              {displayComment()}
            </div>
          )}
        </div>
        <VisualArticleIconbar
          openPriceSuggestDrawer={openPriceSuggestDrawer}
          openReservedQuantityDrawer={openReservedQuantityDrawer}
          displayOnly={displayOnly}
          article={article}
          showLocations={showLocations}
          onDeleteArticle={onDeleteArticle}
          showPrice={showPrice}
          currency={currency}
          revertArticleEdit={revertArticleEdit}
          parentEl={parentEl}
        />
      </Box>
    </div>
  )
})
